import { Box, Flex, Input, Stack, VStack } from "@chakra-ui/react";
import GreyBlackField from "components/DataDisplay/GreyBlackField";
import { Heading18 } from "components/Heading";
import Select from "components/Select";
import { yupResolver } from "@hookform/resolvers/yup";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { useForm, UseFormReturn } from "react-hook-form";
import { PrimaryButton, SecondaryButton } from "components/Button";
import { prescheduledRecurringEventTimingSchema } from "./schema";
import { FloatingField } from "design-system";
import moment from "moment-timezone";
import { meetingTemplateRecurringEventTimingSummary } from "features/Resources/MeetingTemplate/helpers";
import { RRule, Weekday } from "rrule";
import { isEqual, upperFirst } from "lodash";
import { dateInFormat, getDayOfWeek, getLocalTimezone, getNthWeekdayOfMonth } from "helpers/time";
import ExternalLink from "components/Link";
import MeetingDurationField from "../../MeetingDurationField";

/**
 * Build recurrence rule options based on the series start date and timezone
 * @param seriesStartsAt - The date string when the recurring series starts
 * @param timezone - The IANA timezone for the event (e.g., "America/New_York")
 * @param selectedRule - Optional currently selected recurrence rule to include as custom option
 * @returns Select field configuration with recurrence rule options
 */
const REPEATS_FIELD = (seriesStartsAt: string, timezone: string, selectedRule?: string[]) => {
  const tz = timezone || "UTC";
  const { nth } = getNthWeekdayOfMonth(seriesStartsAt, tz);
  const dayIndex = (moment.tz(seriesStartsAt, timezone).day() + 6) % 7;

  const weeklyRule = new RRule({ freq: RRule.WEEKLY, byweekday: [new Weekday(dayIndex)] });
  const biweeklyRule = new RRule({ freq: RRule.WEEKLY, interval: 2, byweekday: [new Weekday(dayIndex)] });
  const monthlyRule = new RRule({ freq: RRule.MONTHLY, byweekday: [new Weekday(dayIndex, nth)] });

  const toReadableValue = (value: string[]) =>
    meetingTemplateRecurringEventTimingSummary({
      recurrenceRules: value,
      seriesStartsAt,
      excludeTime: true,
      timezone,
    });

  const options = [weeklyRule, biweeklyRule, monthlyRule].map((rRule) => {
    const rule = rRule.toString().split("\n");
    return {
      label: toReadableValue(rule),
      value: rule,
    };
  });
  // If there's a selected rule that doesn't match any of our standard options, add it as custom
  if (selectedRule && selectedRule.length > 0) {
    const ruleString = selectedRule[0];
    const isStandardRule = options.some((opt) => opt.value[0] === ruleString);

    if (!isStandardRule) {
      try {
        options.push({
          label: upperFirst(toReadableValue(selectedRule)),
          value: selectedRule,
        });
      } catch (error) {
        // If parsing fails, fall back to showing the raw rule string
        console.error("Failed to parse RRULE:", error);
        options.push({
          label: `Custom: ${ruleString}`,
          value: selectedRule,
        });
      }
    }
  }

  return {
    type: "SELECT",
    label: "Repeats",
    placeholder: "Repeats",
    options,
  };
};

const STARTS_AT_FIELD = {
  type: "datetime-local",
  label: "Starts",
  placeholder: "Starts",
  name: "seriesStartsAt",
};

/**
 * Generate the default weekly recurrence rule for a given date and timezone
 * @param seriesStartsAt - The date string when the recurring series starts
 * @param timezone - The IANA timezone for the event
 * @returns The default weekly recurrence rule
 */
const getDefaultRecurrenceRule = (seriesStartsAt: string, timezone: string): string[] => {
  const dayIndex = (moment.tz(seriesStartsAt, timezone).day() + 6) % 7;
  const weeklyRule = new RRule({ freq: RRule.WEEKLY, byweekday: [new Weekday(dayIndex)] });
  return weeklyRule.toString().split("\n");
};

export default function TimingForm({ form, onClose }: { form: UseFormReturn<any>; onClose: () => void }) {
  const parentValues = form.getValues();
  const defaultStartsAt = moment().startOf("day").hour(9).toISOString();
  const defaultTimezone = getLocalTimezone();
  const startsAt = parentValues.seriesStartsAt || defaultStartsAt;
  const tz = parentValues.timezone || defaultTimezone;

  const localForm = useForm({
    mode: "onBlur",
    resolver: yupResolver(prescheduledRecurringEventTimingSchema),
    defaultValues: {
      ...parentValues,
      durationMinutes: parentValues.durationMinutes || 30,
      seriesStartsAt: startsAt,
      timezone: tz,
      recurrenceRules: parentValues.recurrenceRules?.length
        ? parentValues.recurrenceRules
        : getDefaultRecurrenceRule(startsAt, tz),
    },
  });
  const {
    setValue,
    control,
    watch,
    handleSubmit,
    formState: { errors },
  } = localForm;

  const onSubmit = useCallback(
    (localValues: any) => {
      const fields = ["durationMinutes", "seriesStartsAt", "recurrenceRules", "timezone"];
      fields.forEach((field) => {
        form.setValue(field, localValues[field], { shouldDirty: true });
      });

      onClose?.();
    },
    [form, onClose],
  );

  const [seriesStartsAt, recurrenceRules, timezone] = watch(["seriesStartsAt", "recurrenceRules", "timezone"]);

  const [recurringEventExternalUrl] = form.watch(["recurringEventExternalUrl"]);

  // Track the previous day of week to detect when it changes
  const previousDayOfWeekRef = useRef<string | null>(null);

  // Reset recurrence rules to first option when day of week changes
  useEffect(() => {
    if (!seriesStartsAt || !timezone) return;

    const currentDayOfWeek = getDayOfWeek(seriesStartsAt, timezone);

    // If this is the first render, just store the current day
    if (previousDayOfWeekRef.current === null) {
      previousDayOfWeekRef.current = currentDayOfWeek;
      return;
    }

    // If the day of week changed, reset to the first (weekly) option
    if (previousDayOfWeekRef.current !== currentDayOfWeek) {
      const defaultRule = getDefaultRecurrenceRule(seriesStartsAt, timezone);
      setValue("recurrenceRules", defaultRule, { shouldDirty: true });
      previousDayOfWeekRef.current = currentDayOfWeek;
    }
  }, [seriesStartsAt, timezone, setValue]);

  const repeatsField = useMemo(
    () => REPEATS_FIELD(seriesStartsAt, timezone, recurrenceRules),
    [seriesStartsAt, timezone, recurrenceRules],
  );

  return (
    <Stack py="6" p="2">
      <Heading18 mb={4}>Meeting Timing</Heading18>

      <VStack spacing="16px">
        <MeetingDurationField form={localForm} fieldName="durationMinutes" label="Duration (minutes)" isRequired />
        <GreyBlackField label="Series Starts At" width="100%">
          <Input
            type={STARTS_AT_FIELD.type}
            name="seriesStartsAt"
            value={dateInFormat(seriesStartsAt, "YYYY-MM-DDTHH:mm")}
            onChange={(event: any) => {
              if (event.target.value) {
                setValue(
                  "seriesStartsAt",
                  moment(event.target.value)
                    .tz(timezone as string)
                    .toISOString(),
                  { shouldDirty: true },
                );
              }
            }}
          />
        </GreyBlackField>
        <FloatingField name="timezone" errors={errors.timezone?.message as string | undefined} required>
          <FloatingField.Label>Timezone</FloatingField.Label>
          <FloatingField.Timezone control={control} name="timezone" />
        </FloatingField>
        <GreyBlackField label="Repeats" width="100%">
          <Box w="100%">
            <Select
              isClearable={false}
              required
              options={repeatsField.options}
              value={repeatsField.options.find((option) => isEqual(option.value, recurrenceRules))}
              onChange={(selected: any) => {
                if (selected) {
                  setValue("recurrenceRules", selected.value, { shouldDirty: true });
                }
              }}
              errors={errors}
              control={control}
            />
          </Box>
        </GreyBlackField>
      </VStack>

      {recurringEventExternalUrl && (
        <Box pt={2}>
          <ExternalLink
            w="fit-content"
            text="Need more custom options? Adjust in GCal"
            to={recurringEventExternalUrl}
            linkProps={{ target: "_blank" }}
          />
        </Box>
      )}

      <Flex pt="4" w="100%" justify="flex-end" gap="2">
        <SecondaryButton onClick={onClose}>Cancel</SecondaryButton>
        <PrimaryButton onClick={handleSubmit(onSubmit, console.error)}>Save</PrimaryButton>
      </Flex>
    </Stack>
  );
}
